import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

import { type AudienceOptionType } from '@/api/audience';
import {
  type GoalInputType,
  type GoalKPIMetricType,
  type NotificationPageMeta,
  type NotificationPageParamsType,
  type NotificationSubscriptionGoalDataType,
  mutateGoalCreate,
  mutateGoalUpdate,
  useNotificationMetaKPILabel,
} from '@/api/settings';
import { type UserOptionType } from '@/api/user';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import {
  NumberInput,
  NumberInputGroup,
  NumberInputInput,
  NumberInputTrigger,
  useAppForm,
} from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import {
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/Form/pickers/primitives/select';
import { DateISOFormat } from '@/constants';
import { useHeaderContext } from '@/hooks/header/HeaderContext';

import NotificationSubscriptionCommonFields from './NotificationSubscriptionCommonFields';

type Props = {
  subscription?: NotificationSubscriptionGoalDataType | null;
  audience?: AudienceOptionType;
  notificationPageMeta?: NotificationPageMeta;
  notificationParams?: NotificationPageParamsType;
  onCancel: () => void;
  onSaveComplete: (success: boolean) => void;
};

type GoalValues = {
  name: string;
  recipients: UserOptionType[];
  kpi: GoalKPIMetricType;
  goal: number | null;
  goalStartDate: Date | undefined;
};

const variableTemplateSchema = z.record(z.string(), z.string());

const getGoalFormSchema = () =>
  z.object({
    name: z.string().min(1, t`Name is required`),
    recipients: z.custom<UserOptionType[]>((value) => Array.isArray(value)),
    kpi: z
      .custom<GoalKPIMetricType>()
      .refine((value) => value.value.length > 0, t`KPI is required`),
    goal: z
      .number()
      .nullable()
      .refine(
        (value) => value != null && Number.isFinite(value) && value > 0,
        t`Goal cannot be zero`,
      )
      .transform((value) => value as number),
    goalStartDate: z.date({ message: t`Date is required` }),
  });

const GoalNotificationSubscriptionDialog = ({
  subscription,
  audience,
  notificationPageMeta,
  notificationParams,
  onCancel,
  onSaveComplete,
}: Props) => {
  const { title } = useHeaderContext();
  const getKPILabel = useNotificationMetaKPILabel();
  const isEditing = subscription?.id != null;
  const formSchema = getGoalFormSchema();

  const kpiMetricOptions = useMemo<GoalKPIMetricType[]>(() => {
    if (notificationPageMeta?.kpiMetrics) {
      return notificationPageMeta.kpiMetrics.map((metricOption) => ({
        value: metricOption.value,
        label: metricOption.label,
        allowsAudience: metricOption.allowsAudience,
      }));
    }

    if (isEditing && subscription?.kpiId) {
      return [
        {
          value: subscription.kpiId,
          label: getKPILabel(subscription.kpiId),
          allowsAudience: false,
        },
      ];
    }

    return [];
  }, [getKPILabel, isEditing, notificationPageMeta, subscription?.kpiId]);

  const initialKpi = kpiMetricOptions.find((option) => option.value === subscription?.kpiId) ??
    kpiMetricOptions[0] ?? {
      value: '',
      label: '',
      allowsAudience: false,
    };

  const buildDefaultReportName = (selectedKpiOption?: GoalKPIMetricType | null) => {
    const reportNameParts = [
      t`${selectedKpiOption ? `${selectedKpiOption.label} ` : ''}Goal Reached!`,
    ];
    if (title) {
      reportNameParts.push(title);
    }
    if (selectedKpiOption?.allowsAudience) {
      reportNameParts.push(audience?.name ?? t`TAM`);
    }
    return reportNameParts.join(' - ');
  };
  const initialAutoReportName = buildDefaultReportName(initialKpi);
  const previousAutoReportNameRef = useRef(initialAutoReportName);

  const form = useAppForm({
    defaultValues: {
      name: subscription?.name ?? (isEditing ? '' : initialAutoReportName),
      recipients: subscription?.additionalRecipients ?? [],
      kpi: initialKpi,
      goal: subscription?.goal ?? null,
      goalStartDate: subscription?.goalStartDate
        ? dayjs(dayjs.utc(subscription.goalStartDate).format('YYYY-MM-DD')).toDate()
        : dayjs().startOf('d').toDate(),
    } satisfies GoalValues,
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        const parsed = formSchema.parse(value);
        let success = false;
        const goalStartDate = dayjs(parsed.goalStartDate).format(DateISOFormat) + 'T00:00:00Z';
        const goalInput: Omit<GoalInputType, 'urlTemplate'> = {
          name: parsed.name,
          additionalRecipients: parsed.recipients.map((recipient) => recipient.id),
          goal: parsed.goal,
          kpiId: parsed.kpi.value,
          goalStartDate,
          variableTemplate: JSON.stringify({
            ...notificationParams,
            startDate: goalStartDate,
            endDate: '%%now%%',
          }),
        };

        try {
          if (isEditing && subscription) {
            const parsedVariableTemplate = variableTemplateSchema.parse(
              JSON.parse(subscription.variableTemplate),
            );
            parsedVariableTemplate.startDate = goalStartDate;

            const response = await mutateGoalUpdate({
              ...goalInput,
              id: subscription.id,
              urlTemplate: subscription.urlTemplate,
              variableTemplate: JSON.stringify(parsedVariableTemplate),
            });

            success = !!response;
          } else {
            const response = await mutateGoalCreate({
              ...goalInput,
              urlTemplate: window.location.pathname + window.location.search,
            });

            success = !!response;
          }
        } catch {
          success = false;
        }

        onSaveComplete(success);
      },
    },
  });

  const isSaving = useStore(form.store, (state) => state.isSubmitting);
  const selectedKpi = useStore(form.store, (state) => state.values.kpi);
  const reportName = useStore(form.store, (state) => state.values.name);
  const autoReportName = buildDefaultReportName(selectedKpi);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const previousAutoReportName = previousAutoReportNameRef.current;
    const shouldSyncName = reportName === previousAutoReportName;

    previousAutoReportNameRef.current = autoReportName;

    if (shouldSyncName && reportName !== autoReportName) {
      form.setFieldValue('name', autoReportName);
    }
  }, [autoReportName, form, isEditing, reportName]);

  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (isSaving) return;
        if (!isOpen) handleCancel();
      }}
    >
      <DialogContent showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? <Trans>Edit Goal Notification</Trans> : <Trans>Goal Notification</Trans>}
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Goal Notification sends a single email to all recipients when a goal is reached for
              one of the metrics on this page.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <div className="grid grid-cols-12 gap-4">
            <NotificationSubscriptionCommonFields
              form={form}
              fields={{
                name: 'name',
                recipients: 'recipients',
              }}
              nameInputId="goal-notification-name"
              recipientsInputId="goal-notification-recipients"
              isSubmitting={isSaving}
            />
            <form.AppField name="kpi">
              {(field) => (
                <field.Field className="col-span-12 md:col-span-5">
                  <FieldLabel htmlFor="goal-notification-kpi">
                    <Trans>KPI</Trans>
                  </FieldLabel>
                  <field.Select items={kpiMetricOptions} disabled={isEditing}>
                    <field.SelectTrigger id="goal-notification-kpi" className="w-full">
                      <SelectValue placeholder={t`Choose KPI`} />
                    </field.SelectTrigger>
                    <SelectContent>
                      {kpiMetricOptions.map((item) => (
                        <SelectItem key={item.value} value={item}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </field.Select>
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
            <form.AppField name="goal">
              {(field) => (
                <field.Field className="col-span-6 md:col-span-3">
                  <FieldLabel htmlFor="goal-notification-goal">
                    <Trans>Goal</Trans>
                  </FieldLabel>
                  <NumberInput
                    id="goal-notification-goal"
                    value={field.state.value}
                    disabled={isSaving}
                    min={0}
                    format={{ useGrouping: true }}
                    onValueChange={field.handleChange}
                  >
                    <NumberInputGroup>
                      <NumberInputInput
                        placeholder={t`Enter Goal`}
                        aria-invalid={
                          field.state.meta.isTouched && !field.state.meta.isValid ? true : undefined
                        }
                        onBlur={field.handleBlur}
                      />
                      <NumberInputTrigger />
                    </NumberInputGroup>
                  </NumberInput>
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
            <form.AppField name="goalStartDate">
              {(field) => (
                <field.Field className="col-span-6 md:col-span-4">
                  <FieldLabel htmlFor="goal-notification-goal-start-date">
                    <Trans>Goal Start Date</Trans>
                  </FieldLabel>
                  <field.DatePicker
                    id="goal-notification-goal-start-date"
                    className="w-full"
                    align="end"
                    disabled={isSaving}
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
          </div>
        </form>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large" disabled={isSaving}>
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <form.AppForm>
            <form.SubmitButton size="large" variant="on-primary">
              <Trans>Save</Trans>
            </form.SubmitButton>
          </form.AppForm>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoalNotificationSubscriptionDialog;
